import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';
import {Router} from '@angular/router';
import * as dayjs from 'dayjs';
import {CustomerFeatureTypeEnum} from 'src/app/enums/AdEnums';
import {EventActionEnum, EventCategoryEnum} from 'src/app/enums/GaTrackingEnums';
import {ToolsService} from 'src/app/services/tools.service';
import {UrlBuilderService} from 'src/app/services/url-builder.service';
import {CareproviderMapItemTranslated} from 'src/app/types/CareproviderMapItemTranslated';
import {CareService} from 'src/app/types/CareService';
import {MapData} from 'src/app/types/MapData';
import {SearchResultsCareproviderUnitItem} from 'src/app/types/SearchResultsCareproviderUnitItem';
import {SearchState} from 'src/app/types/SearchState';
import {DLPageTypeEnum} from '../../../../../../enums/DataLayerEnums';
import {GtmEventEnum} from '../../../../../../enums/GoogleEnums';
import {ModalTypeEnum} from '../../../../../../enums/ModalEnums';
import {RoutesAddonsEnums} from '../../../../../../enums/RoutesEnums';
import {AppConfigService} from '../../../../../../services/app-config.service';
import {GtmGa4EventPushService} from '../../../../../../services/gtm-ga4-event-push.service';
import {GtmService} from '../../../../../../services/gtm.service';
import {ModalsControlService} from '../../../../../../services/modals-control.service';
import {DataLayerParamsClickGA4} from '../../../../../../types/DataLayerParamsClickGA4';
import {ModalCommand} from '../../../../../../types/ModalCommand';

@Component({
  selector: 'app-careprovider-unit-item-hsa',
  templateUrl: './careprovider-unit-item-hsa.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CareproviderUnitItemHsaComponent {
  careproviderUnit = input<SearchResultsCareproviderUnitItem>();
  itemIndex = input<number>();
  searchState = input<SearchState>();
  pageType = input<DLPageTypeEnum>();
  careproviderUrl = computed<string>(() =>
    this.urlBuilder.CreateStaticLink(
      this.urlBuilder.CreateCareproviderUnitRouterLink(this.careproviderUnit()?.id, this.careproviderUnit()?.urlPath),
    ),
  );

  showHomepage = computed(
    () =>
      this.careproviderUnit().caregiverContactInfo &&
      this.careproviderUnit().caregiverContactInfo.websiteUrl &&
      this.careproviderUnit().caregiverContactInfo.websiteUrl !== '',
  );
  showPhone = computed(
    () =>
      this.careproviderUnit().caregiverContactInfo &&
      this.careproviderUnit().caregiverContactInfo.phoneNumbers &&
      this.careproviderUnit().caregiverContactInfo.phoneNumbers.length > 0,
  );

  hasImage = computed(
    () => !this.tools.isNil(this.careproviderUnit().thumbnail) && this.careproviderUnit().thumbnail !== '',
  );
  imageUrl = computed(() =>
    this.hasImage() ? `${this.appConfigService.appConfig.amazonS3Url}${this.careproviderUnit().thumbnail}` : null,
  );
  logoUrl = computed<string>(() => {
    if (!this.careproviderUnit() || !this.careproviderUnit().logoUrl) {
      return null;
    }
    return this.tools.getImageUrl(this.careproviderUnit().logoUrl);
  });
  isPriorityImage = computed(() => this.itemIndex() === 0);
  featureLabel = computed(() => `${this.careproviderUnit().name}-${this.careproviderUnit().id}`);
  listaDigDirectCF = computed(() =>
    this.careproviderUnit().customerFeatures.find(
      (feature) => feature.type === CustomerFeatureTypeEnum.LISTA_DIG_DIRECT,
    ),
  );
  listaDigVarden = computed(() =>
    this.careproviderUnit().customerFeatures.find(
      (feature) => feature.type === CustomerFeatureTypeEnum.LISTA_DIG_VARDEN,
    ),
  );
  mainSegment = computed(() => this.careproviderUnit()?.mainCaregiverUnitType?.segment?.name?.toLowerCase());
  address = computed(() => {
    const name = this.careproviderUnit().place?.name;
    return name ? name : this.careproviderUnit().address?.latitude ? 'Se på kartan' : null;
  });
  customGa4Params = computed<DataLayerParamsClickGA4>(() => this.buildDataLayerParamsClickGA4(this.careproviderUnit()));

  public eventCategoryEnum = EventCategoryEnum;
  public eventActionEnum = EventActionEnum;

  constructor(
    private urlBuilder: UrlBuilderService,
    private tools: ToolsService,
    private router: Router,
    private modalControl: ModalsControlService,
    private gtmService: GtmService,
    private gtmEventPush: GtmGa4EventPushService,
    private appConfigService: AppConfigService,
  ) {
    dayjs.locale('sv');
  }

  public openMap(): void {
    const markers = this.getMarker();
    this.showMap(markers);
  }

  public openCpruContact(): void {
    const targetUrl = `${this.careproviderUrl()}#${RoutesAddonsEnums.CAREPROVIDER_CONTACT}`;
    window.location.assign(targetUrl);
  }

  public generateDataLayerParamsClickGA4(
    callToAction: string,
    careService: CareService,
    index: number,
  ): DataLayerParamsClickGA4 {
    const params: DataLayerParamsClickGA4 = this.tools.clone(this.customGa4Params());
    if (careService) {
      params.careServiceId = careService.id;
      params.careServiceTypeId = careService.careServiceTypeId;
    }
    params.callToAction = callToAction;
    if (
      careService &&
      !this.tools.isNil(careService.bookingConfigurationData) &&
      !this.tools.isNil(
        careService.bookingConfigurationData[index] &&
          !this.tools.isNil(careService.bookingConfigurationData[index].bookingSettings),
      )
    ) {
      params.bookingType = careService.bookingConfigurationData[index].bookingSettings.bookingType.name;
    }
    return this.tools.clone(params);
  }

  private showMap(markers: Array<CareproviderMapItemTranslated>) {
    if (markers.length > 0) {
      const modalCommand: ModalCommand<MapData> = {
        type: ModalTypeEnum.GOOGLE_MAP,
        data: {height: 80, markers},
      };
      this.modalControl.openModal(modalCommand);
      this.pushGtmTag('show map');
    } else {
      this.pushGtmTag('map not shown');
    }
  }

  private pushGtmTag(message: string) {
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: 'Search result',
      Action: message,
      Label: '',
      Value: 1,
    };

    this.gtmService.pushTag(gtmTag);

    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `Search result - ${message}`,
      cpruId: this.careproviderUnit().id,
      pageType: this.pageType(),
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);
  }

  private getMarker(): Array<CareproviderMapItemTranslated> {
    const markers: Array<CareproviderMapItemTranslated> = [];

    if (
      this.careproviderUnit() &&
      this.careproviderUnit().address &&
      this.careproviderUnit().address.latitude &&
      this.careproviderUnit().address.latitude !== 0
    ) {
      if (this.careproviderUnit().address.street === null) {
        this.careproviderUnit().address.street = '';
      }
      if (this.careproviderUnit().address.city === null) {
        this.careproviderUnit().address.city = '';
      }
      markers.push({
        id: this.careproviderUnit().id,
        name: this.careproviderUnit().name,
        urlPath: this.careproviderUnit().urlPath,
        location: {
          lat: this.careproviderUnit().address.latitude,
          lng: this.careproviderUnit().address.longitude,
        },
        rating: this.careproviderUnit().rating,
        address: this.careproviderUnit().address,
        thumbnail: this.careproviderUnit().thumbnail,
      });
    }
    return markers;
  }

  private buildDataLayerParamsClickGA4(careproviderUnit: SearchResultsCareproviderUnitItem): DataLayerParamsClickGA4 {
    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: '',
      cpruId: careproviderUnit.id,
      inferredCareProviderTypeAreaId: careproviderUnit.mainCaregiverUnitType?.area?.id,
      mainCareProviderTypeId: careproviderUnit.mainCaregiverUnitType?.id,
      inferredCategoryTypeId: careproviderUnit.mainCaregiverUnitType?.id,
      pageType: this.pageType(),
    };
    if (careproviderUnit.bookingSettings) {
      careproviderUnit.bookingSettings.forEach((bookingSetting) => {
        if (!this.tools.isNil(bookingSetting) && !this.tools.isNil(bookingSetting.bookingType)) {
          customGa4Params.bookingType = bookingSetting.bookingType.name;
        }
      });
    }
    return customGa4Params;
  }
}
